import { Components, Effects, Synths, type Sample } from 'Scream';
import { audioContext } from './context';
import { analyser } from './analyser';
import { openInput, releaseInput } from './devices';
import { TapeDeck } from './TapeDeck';
import { encodeWav, saveBlob } from './wav';

export const DEFAULT_SAMPLE = './assets/samples/01_Piano.mp3';

/**
 * Effect slots, in the order they are chained. `PanelActions` used to address
 * these by bare index; naming them keeps the wiring honest.
 */
export const EFFECT = { ECHO: 0, FUZZ: 1, FILTER: 2, REVERB: 3 } as const;

export const vss30 = new Synths.VSS30(audioContext);
vss30.addEffect(Effects.Delay);
vss30.addEffect(Effects.Saturate);
vss30.addEffect(Effects.Filter);
vss30.addEffect(Effects.Reverb);
vss30.connectEffects();
vss30.connect(audioContext.destination);
vss30.connect(analyser);

vss30.effects[EFFECT.REVERB].wet.gain.setTargetAtTime(3, audioContext.currentTime, 0.01);

export const recorder = new TapeDeck(
  audioContext,
  { file: 'VSS30_', mimeType: 'audio/webm', codec: 'opus', extension: 'webm' },
  'AUDIO_ONLY',
);
vss30.connect(recorder.input);

/**
 * Scream's `Sample` asks for the microphone itself, and always for the default
 * one. Swapping its `record` for a version that opens the selected device and
 * hands the stream to the same internal path leaves the timing alone — the
 * permission prompt still lands when a take starts — while honouring the pick.
 */
function captureFromSelectedInput(sample: Sample): Sample {
  sample.record = (cb) => {
    void openInput().then((stream) => sample.recordStream(stream, cb));
  };
  return sample;
}

captureFromSelectedInput(vss30.sample);

/** Sample data already decoded, keyed by path. Filled by the loader. */
const decoded = new Map<string, Float32Array>();

/**
 * Decodes a sample file the loader has fetched and keeps the result, so that
 * picking a slot later is a buffer copy rather than a round trip and a decode.
 */
export async function cacheSample(path: string, bytes: ArrayBuffer): Promise<void> {
  if (decoded.has(path)) return;
  const buffer = await audioContext.decodeAudioData(bytes);
  decoded.set(path, buffer.getChannelData(0));
}

/** Loads a sample file into the synth. Rejects are surfaced, never swallowed. */
export async function loadSample(path: string): Promise<void> {
  const sample = captureFromSelectedInput(new Components.Sample(audioContext));
  const warm = decoded.get(path);
  if (warm) {
    // The tail of Scream's own `load`, with the fetch and the decode already
    // paid for during the warm-up.
    sample.rawBuffer = warm;
    sample.setBuffer();
  } else {
    await sample.load(path);
  }
  vss30.setSample(sample);
}

/** Lets go of the microphone once a take is finished with. */
export function endSampling(): void {
  releaseInput();
}

/**
 * Whatever is loaded in the sampler right now, recorded or loaded from a file.
 * A freshly constructed `Sample` holds a single frame, which is the empty case.
 * Which slot it came from is the panel's business, not this module's.
 */
export function sampleBuffer(): Float32Array | null {
  const raw = vss30.sample?.rawBuffer;
  return raw && raw.length > 1 ? raw : null;
}

/** Writes the loaded take out as a WAV file. Returns false if there is none. */
export function saveSample(): boolean {
  const raw = sampleBuffer();
  if (!raw) return false;
  saveBlob(encodeWav(raw, audioContext.sampleRate), `VSS30_Sample_${Date.now()}.wav`);
  return true;
}
